import { Paper, Typography, Box } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useSubscriptionsReport } from "../../hooks/useReportDashboard";
import { ErrorRetry } from "../../components/common/ErrorRetry";
import { KPICard } from "../../components/reportDashboard/reportSummary/KPICard";
import KpiCardLoadingShimmer from "../../components/common/reportDashBShimmer/KpiCardLoadingShimmer";
import { mapSubscriptionSummaryToKpis } from "../../utils/subscriptionSummary";

const PLAN_COLORS = ["#94A3B8", "#1F4FFF", "#9333EA", "#16A34A", "#F59E0B"];

const formatPlanLabel = (value = "") =>
  value
    .replace(/([A-Z])/g, " $1")
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(value);

export default function DashboardOverview() {
  const {
    data: subscriptionReportsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useSubscriptionsReport();

  const revenueData = Object.entries(
    subscriptionReportsData?.data?.mrrBySubscriptionPlan || {},
  ).map(([planName, planData]) => ({
    name: formatPlanLabel(planName),
    revenue: planData?.monthlyRevenue || 0,
    subscribers: planData?.subscriberCount || 0,
    growthPercentage: planData?.growthPercentage || 0,
  }));

  const planDistributionData = Object.entries(
    subscriptionReportsData?.data?.planDistributionVolume || {},
  ).map(([planName, value], index) => ({
    name: formatPlanLabel(planName),
    value: Number(value) || 0,
    color: PLAN_COLORS[index % PLAN_COLORS.length],
  }));

  const kpiData = mapSubscriptionSummaryToKpis(
    subscriptionReportsData?.data?.subscriptionSummary,
  );

  return (
    <Box>
      {/* KPI Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 3,
          mb: 4,
        }}
      >
        {isLoading ? (
          Array.from({ length: 4 }).map((_, idx) => (
            <KpiCardLoadingShimmer key={idx} />
          ))
        ) : isError ? (
          <Box sx={{ gridColumn: "1 / -1" }}>
            <ErrorRetry
              message={error?.message || "Unable to load subscription summary"}
              onRetry={refetch}
            />
          </Box>
        ) : kpiData.length > 0 ? (
          kpiData.map((kpi, index) => (
            <KPICard
              key={kpi.label || index}
              label={kpi.label}
              value={kpi.value}
              status={kpi.status}
              changePercentage={kpi.changePercentage}
              history={kpi.history}
              secondaryValue={kpi.secondaryValue}
              currency={kpi.currency}
            />
          ))
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
              gridColumn: "1 / -1",
            }}
          >
            <Typography>No subscription summary available.</Typography>
          </Paper>
        )}
      </Box>

      {/* Revenue Analytics Section */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
          gap: 3,
        }}
      >
        {/* Revenue Chart */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            height: "450px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" className="font-semibold mb-4">
            Revenue Analytics
          </Typography>
          <Box sx={{ flex: 1, minHeight: 350, minWidth: 200 }}>
            {isError ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <ErrorRetry
                  message={error?.message || "Unable to load revenue analytics"}
                  onRetry={refetch}
                />
              </Box>
            ) : isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography>Loading revenue analytics...</Typography>
              </Box>
            ) : revenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={200}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#64748B" />
                  <YAxis stroke="#64748B" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                    }}
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `${label} plan`}
                  />
                  <Legend />
                  <Bar
                    dataKey="revenue"
                    fill="#1F4FFF"
                    name="Monthly Revenue"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography className="text-slate-500">
                  No revenue analytics available
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Plan Distribution */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            minHeight: "450px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" className="font-semibold mb-4">
            Plan Distribution
          </Typography>
          <Box
            sx={{
              height: 250,
              minHeight: 250,
              minWidth: 200,
              flexShrink: 0,
            }}
          >
            {isError ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <ErrorRetry
                  message={error?.message || "Unable to load plan distribution"}
                  onRetry={refetch}
                />
              </Box>
            ) : isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography>Loading plan distribution...</Typography>
              </Box>
            ) : planDistributionData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={200}>
                <PieChart>
                  <Pie
                    data={planDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {planDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => Number(value).toLocaleString()}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography className="text-slate-500">
                  No plan distribution available
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            {planDistributionData.map((plan, index) => (
              <Box
                key={index}
                className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
              >
                <Box className="flex items-center gap-2">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "3px",
                      bgcolor: plan.color,
                    }}
                  />
                  <Typography variant="body2" className="text-slate-700">
                    {plan.name}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  className="font-semibold text-slate-900"
                >
                  {plan.value.toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
